import * as React from 'react';
import LazyLoad from 'react-lazyload';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite';
import { CountLessonAudio, CountLessonCoachVideoPlayback, } from '@lean-body/src/domain';
import { routes } from '@lean-body/src/route';
import { LessonRanking } from '@lean-body/components/organisms';
import { Colors, MediaBreakPointUp, Sizes } from '@lean-body/components/styles';
import { ZIndex } from '../constants';
import { LocalStorage, SEARCH_HISTORIES_MAX_NUM } from '@lean-body/src/infra';
import { isAndroidApp, useQuery, useXlMediaQuery } from '@lean-body/src/util';
import { SearchKeywordBox } from '@lean-body/components/organisms/search/search_keyword_box';
import { SearchLanding } from '../organisms/search/search_landing';
import { MoreLink } from '../atoms/more_link';
import { CountLessonCarousel } from '../organisms/count_lesson_carousel';
import { ProgramComponentCarousel } from '../organisms/program_component_carousel';
import { MerchandiseCarousel } from '../organisms/merchandise_carousel';
import { LessonComponentCarousel } from '../organisms/lesson_component_carousel';
export const SEARCH_SELECTED_TAB = 'selectedTab';
export const SEARCH_SELECTED_OPTION = 'selectedOption';
export const SearchTabs = {
    recommend: 'おすすめ',
    categorySearch: 'カテゴリから探す',
};
export const SearchTabKeys = {
    recommend: 'recommend',
    categorySearch: 'categorySearch',
};
const CAROUSEL_COUNT = 10;
const SERIES_PROGRAMS_FIRST_PAGE = 0;
const MERCHANDISE_FIRST_PAGE = 1;
const RANKING_LIMIT_COUNT = 10;
const RANKING_ITEM_SIZE = 320;
const RANKING_ITEM_SIZE_OVER_XL = 660;
export const LIST_ITEM_MARGIN_RIGHT = 10;
const RECOMMEND_SIDE_MARGIN_SP = 20;
const RECOMMEND_SIDE_MARGIN_MD = 24;
const RECOMMEND_SIDE_MARGIN_XL = 32;
export const MainSearchPage = ({ apiClient, config, tracker }) => {
    const isXL = useXlMediaQuery();
    const query = useQuery();
    const navigate = useNavigate();
    const localStorage = new LocalStorage();
    const [lessonSections, setLessonSections] = useState([]);
    const [seriesProgramsSection, setSeriesProgramsSection] = useState([]);
    const [liveLessonProgramSection, setLiveLessonProgramSection] = useState([]);
    const [searchHistories, setSearchHistories] = useState(localStorage.loadSearchHistories || []);
    const [keyword, setKeyword] = useState();
    const [searchKeywords, setSearchKeywords] = useState([]);
    const [contentStyles, setContentStyles] = useState([]);
    const [partTags, setPartTags] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const [durations, setDurations] = useState([]);
    const [difficulties, setDifficulties] = useState([]);
    const [rankings, setRankings] = useState([]);
    const [merchandises, setMerchandises] = useState([]);
    const [countLessons, setCountLessons] = useState(null);
    const [submitDisabled, setSubmitDisabled] = useState((keyword === null || keyword === void 0 ? void 0 : keyword.length) < 1);
    const tab = SearchTabs[query.get(SEARCH_SELECTED_TAB)] || SearchTabs.recommend;
    useEffect(() => {
        apiClient.getKeywords().then((v) => setSearchKeywords(v.keywords));
    }, []);
    useEffect(() => {
        if (tab === SearchTabs.recommend) {
            if (!lessonSections.length)
                apiClient.fetchLessonSection().then((v) => setLessonSections(v));
            if (!rankings.length)
                apiClient.fetchDailyLessonRanking(RANKING_LIMIT_COUNT).then((v) => setRankings(v));
            if (!merchandises.length) {
                apiClient.getMerchandises(MERCHANDISE_FIRST_PAGE).then((v) => setMerchandises(merchandises.concat(v)));
            }
            if (!seriesProgramsSection.length) {
                apiClient
                    .fetchProgramList(SERIES_PROGRAMS_FIRST_PAGE)
                    .then((programs) => setSeriesProgramsSection(programs.slice(0, CAROUSEL_COUNT)));
            }
            if (!liveLessonProgramSection.length) {
                apiClient.fetchLiveLessonProgramSection().then((v) => setLiveLessonProgramSection(v));
            }
            if (!countLessons)
                apiClient.fetchCountLessons().then(setCountLessons);
        }
        else if (tab === SearchTabs.categorySearch) {
            if (!contentStyles.length)
                apiClient.fetchStyles().then((v) => setContentStyles(v));
            if (!partTags.length)
                apiClient.fetchPartTags().then((v) => setPartTags(v));
            if (!trainers.length)
                apiClient.fetchTrainers().then((v) => setTrainers(v));
            if (!durations.length)
                apiClient.fetchDurationsForSearch().then((v) => setDurations(v));
            if (!difficulties.length)
                apiClient.fetchDifficulties().then((v) => setDifficulties(v));
        }
    }, [tab]);
    useEffect(() => {
        setSubmitDisabled((keyword === null || keyword === void 0 ? void 0 : keyword.length) < 1);
    }, [keyword]);
    const search = (q) => {
        // 検索履歴の保存処理
        saveSearchHistory();
        navigate(`${routes.SEARCH_RESULT.compile()}?q=${q}`);
    };
    const saveSearchHistory = () => {
        // 検索ワードが既に履歴に存在する場合、先頭に移動するので一旦削除する
        if (searchHistories.includes(keyword)) {
            setSearchHistories(searchHistories.filter((his) => his !== keyword));
        }
        // 先頭に検索ワードを追加する
        setSearchHistories([keyword, ...searchHistories]);
        // 履歴件数が最大を超えた場合に最後の検索ワードを削除する
        if (searchHistories.length > SEARCH_HISTORIES_MAX_NUM) {
            setSearchHistories(searchHistories.filter((_, i) => i < searchHistories.length - 1));
        }
        localStorage.saveSearchHistories(searchHistories);
    };
    const onChangeKeyword = (value) => {
        setKeyword(value);
    };
    const onClickKeyword = (q) => {
        setKeyword(q);
        search(q);
    };
    const onSubmit = () => {
        search(keyword);
        tracker.trackSubmitSearch(keyword);
    };
    const onCancel = () => {
        setKeyword('');
    };
    const openCategorySearch = () => {
        navigate(`${routes.SEARCH.compile()}?${SEARCH_SELECTED_TAB}=${SearchTabKeys.categorySearch}`);
    };
    const openRecommend = () => {
        navigate(routes.SEARCH.compile());
    };
    const submitKeyword = (q) => {
        const tokens = q.trim().split(/\s+/).filter(Boolean);
        const nextHistories = tokens
            .reduce((histories, token) => [token, ...histories.filter((history) => history !== token)], searchHistories)
            .slice(0, SEARCH_HISTORIES_MAX_NUM);
        setKeyword(q);
        setSearchHistories(nextHistories);
        localStorage.saveSearchHistories(nextHistories);
        navigate(`${routes.SEARCH_RESULT.compile()}?q=${encodeURIComponent(q)}`);
        tracker.trackSubmitSearch(q);
    };
    const clearSearchHistories = () => {
        setSearchHistories([]);
        localStorage.saveSearchHistories([]);
    };
    // コーチ選択済みならレッスン開始、未選択なら一覧ページ経由でコーチ選択画面を表示する
    const onClickCountLesson = (lessonId) => {
        if ((countLessons === null || countLessons === void 0 ? void 0 : countLessons.selectedCoachId) != null) {
            CountLessonAudio.prepareFromUserGesture();
            navigate(routes.COUNT_LESSON_DETAIL.compile({ id: String(lessonId) }));
        }
        else {
            CountLessonCoachVideoPlayback.prepareFromUserGesture();
            navigate(routes.COUNT_LESSONS.compile());
        }
    };
    return (React.createElement("div", { className: css(styles.container) },
        tab === SearchTabs.recommend ? (React.createElement("div", { className: css(styles.stickySearch) },
            React.createElement("div", { className: css(styles.centerContentContainer) },
                React.createElement(SearchKeywordBox, { keywordValue: keyword, searchKeywords: searchKeywords, searchHistories: searchHistories, submitDisabled: submitDisabled, onChangeKeyword: onChangeKeyword, onSelectedKeyword: onClickKeyword, clear: onCancel, submit: onSubmit, placeholder: "\u30EC\u30C3\u30B9\u30F3\u3092\u691C\u7D22\u3059\u308B", onOpen: openCategorySearch })))) : null,
        tab === SearchTabs.recommend ? (React.createElement("div", { className: css(styles.recommendBody) },
            !!rankings.length && (React.createElement(LazyLoad, null,
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, "\u30C7\u30A4\u30EA\u30FC\u30E9\u30F3\u30AD\u30F3\u30B0"),
                    React.createElement(MoreLink, { to: routes.RANKING.compile() })),
                React.createElement("div", { onClick: () => tracker.trackHomeClick('daily_ranking') },
                    React.createElement(LessonRanking, { rankings: rankings, classObject: styles.sectionContentContainer, addFavoriteLesson: apiClient.addFavoriteLesson, unFavoriteLesson: apiClient.unFavoriteLesson, itemSize: isXL ? RANKING_ITEM_SIZE_OVER_XL : RANKING_ITEM_SIZE, isBig: isXL })))),
            !!lessonSections.length && (React.createElement(React.Fragment, null, lessonSections.map((v, i) => (React.createElement(LazyLoad, { key: i },
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, v.name)),
                React.createElement("div", { onClick: () => tracker.trackHomeClick('lesson_section') },
                    React.createElement(LessonComponentCarousel, { classObject: styles.sectionContentContainer, lessons: v.lessons, addFavoriteLesson: apiClient.addFavoriteLesson, unFavoriteLesson: apiClient.unFavoriteLesson, itemMarginRight: LIST_ITEM_MARGIN_RIGHT, isMin: !isXL }))))))),
            !!(countLessons === null || countLessons === void 0 ? void 0 : countLessons.lessons.length) && (React.createElement(LazyLoad, null,
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, "\u30B5\u30AF\u30C3\u3068\u30AB\u30A6\u30F3\u30C8\u30EC\u30C3\u30B9\u30F3\u23F1\uFE0F")),
                React.createElement("div", { onClick: () => tracker.trackHomeClick('count_lesson') },
                    React.createElement(CountLessonCarousel, { lessons: countLessons.lessons, onClickLesson: onClickCountLesson })))),
            !!seriesProgramsSection.length && (React.createElement(LazyLoad, null,
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, "\u65B0\u7740\u30B7\u30EA\u30FC\u30BA")),
                React.createElement("div", { onClick: () => tracker.trackHomeClick('series_program') },
                    React.createElement(ProgramComponentCarousel, { programs: seriesProgramsSection, classObject: styles.sectionContentContainer, addFavoriteProgram: apiClient.addFavoriteProgram, unFavoriteProgram: apiClient.unFavoriteProgram, itemMarginRight: LIST_ITEM_MARGIN_RIGHT, isMin: !isXL })))),
            !!liveLessonProgramSection.length && (React.createElement(LazyLoad, null,
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, "LIVE\u914D\u4FE1\u30A2\u30FC\u30AB\u30A4\u30D6")),
                React.createElement("div", null,
                    React.createElement(ProgramComponentCarousel, { programs: liveLessonProgramSection, classObject: styles.sectionContentContainer, addFavoriteProgram: apiClient.addFavoriteProgram, unFavoriteProgram: apiClient.unFavoriteProgram, itemMarginRight: LIST_ITEM_MARGIN_RIGHT, isMin: !isXL })))),
            !isAndroidApp() && !!merchandises.length && (React.createElement(LazyLoad, null,
                React.createElement("div", { className: css(styles.sectionHeader) },
                    React.createElement("div", { className: css(styles.sectionTitle) }, "\u304A\u3059\u3059\u3081\u30A2\u30A4\u30C6\u30E0"),
                    React.createElement(MoreLink, { to: routes.MERCHANDISE.compile() })),
                React.createElement(MerchandiseCarousel, { merchandises: merchandises, classObject: styles.sectionContentContainer, itemMarginRight: LIST_ITEM_MARGIN_RIGHT, tracker: tracker }))))) : (tab === SearchTabs.categorySearch && (React.createElement(SearchLanding, { config: config, keyword: keyword || '', searchHistories: searchHistories, styles: contentStyles, partTags: partTags, trainers: trainers, durations: durations, difficulties: difficulties, onChangeKeyword: onChangeKeyword, onSubmitKeyword: submitKeyword, onClearHistories: clearSearchHistories, onBack: openRecommend })))));
};
const styles = StyleSheet.create({
    container: {
        width: '100%',
        margin: '0 auto',
        paddingBottom: 30,
        [MediaBreakPointUp.MD]: {
            marginBottom: 40,
        },
    },
    stickySearch: {
        position: 'sticky',
        top: Sizes.MobileHeaderHeight,
        zIndex: ZIndex.Header,
        backgroundColor: Colors.White,
        paddingTop: 16,
        paddingBottom: 24,
        [MediaBreakPointUp.MD]: {
            top: 0,
            paddingTop: 38,
        },
    },
    recommendBody: {
        marginTop: -24,
    },
    centerContentContainer: {
        textAlign: 'center',
        width: '100%',
        padding: '0 16px',
        [MediaBreakPointUp.MD]: {
            margin: 'auto',
            padding: 0,
            maxWidth: 600,
        },
        [MediaBreakPointUp.XL]: {
            maxWidth: 1024,
        },
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 62,
        padding: `24px ${RECOMMEND_SIDE_MARGIN_SP}px 12px`,
        [MediaBreakPointUp.MD]: {
            padding: `24px ${RECOMMEND_SIDE_MARGIN_MD}px 12px`,
        },
        [MediaBreakPointUp.XL]: {
            padding: `24px ${RECOMMEND_SIDE_MARGIN_XL}px 12px`,
        },
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: '160%',
        letterSpacing: 1.28,
    },
    sectionContentContainer: {
        padding: `0 ${RECOMMEND_SIDE_MARGIN_SP}px`,
        [MediaBreakPointUp.MD]: {
            padding: `0 ${RECOMMEND_SIDE_MARGIN_MD}px`,
        },
        [MediaBreakPointUp.XL]: {
            padding: `0 ${RECOMMEND_SIDE_MARGIN_XL}px`,
        },
    },
});
